import React, { ReactElement, useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PaperComponent from "shared-components/paper-component";
import { PRIMARY_REGULAR_FONT } from "constants/style/fonts";
import { RootState } from "redux-store/store";
import { useSnackbar } from "notistack";
import {
  NotificationContainer,
  FieldContainer,
  InputLabelText,
  InputTextField,
  NotificationTypeDropdown,
  UserTypeDropdown,
  ActionButtonsRow,
} from "pages/management/notification/add-notification/styles";
import {
  ImgWholeContainer,
  ImageFlexContainer,
  ImageInput,
  AddImageContainer,
  ChildImageContainer,
  ChildImage,
  RemoveImageLogo,
  ImgWholeContainerDeals,
} from "pages/location/components/add-location/styles";
import AddImage from "shared-components/icons/add-image";

import ArabicInput from "shared-components/arabic-input/textfield";
import EnterArabicText from "shared-components/arabic-input/enter-in-arabic-text";
import LoaderButton from "shared-components/loader-button";

import { MenuItem, FormControl, RadioGroup, FormControlLabel, Radio, Checkbox } from "@material-ui/core";

import {
  EMPTY_INPUT,
  INVALID_LENGTH_LABEL_INPUT,
  INVALID_LENGTH_TITLE_INPUT,
} from "constants/snackbar-messages-constants";
import { NOTIFICATION } from "constants/route/route-constants";

import {
  ErrorHelperText,
  Required,
} from "pages/best-deals/components/new-deal/styles";
import { CancelButton, CancelButtonSec } from "pages/management/privacy-policy/components/details/styles";
import { loaderBtnStyleFunc } from "constants/style/props";
import {
  addNewNotificationThunk,
  // setCurrentNotificationReducer,
} from "slices/notifications";
import { useAppDispatch, useAppSelector } from "redux-store/hooks";
import { NotificationObject } from "interfaces/notifications-interfaces";
import { resolveImageUrlForApi } from "utils/image-url-utils";
import MaterialUIPickers from "pages/best-deals/components/date-time";
import CustomRichTextEditor from "shared-components/rich-text-editor";
import {
  ALL_PLUGINS,
  FAQ_QUICK_ACCESS_BLOCK,
  FULL_TOOLBAR,
} from "constants/text-editor-tools";
import { CancelInputDescription } from "pages/best-deals/components/new-deal/styles";
import { CancelInput } from "shared-components/arabic-input/textfield/styles";

const ACTION_TYPE_NONE = "None";
const ACTION_TYPE_LINK = "Link";
const ACTION_TYPE_BUTTON = "Button";
const ACTION_LABEL_MAX_LENGTH = 50;
const EXTRA_INFO_REDIRECTION_IN_APP = "In-App";
const NOTIFICATION_BODY_MAX_LENGTH = 500;
const NOTIFICATION_BODY_LENGTH_ERROR = `must be less than ${NOTIFICATION_BODY_MAX_LENGTH} character`;
const SUB_TEXT_TOOLBAR = "bold italic";

function getRawText(htmlStr: string | null | undefined): string {
  if (!htmlStr) return "";
  return htmlStr
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function getRawTextLength(htmlStr: string | null | undefined): number {
  return getRawText(htmlStr).length;
}

const APP_SCREEN_REDIRECTION_OPTIONS = [
  "Book Service",
  "Test Drive",
  "Request a Quote",
  "None",
];
const DEFAULT_REDIRECTION_SCREEN = "None";

function convertBahrainTimeToUtc(d: Date): Date {
  const year = d.getFullYear();
  const month = d.getMonth();
  const date = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  // Bahrain timezone offset is UTC+3.
  // Converting local Bahrain time (year, month, date, hours, minutes) to UTC:
  return new Date(Date.UTC(year, month, date, hours - 3, minutes, 0, 0));
}

const userArr = ["All", "Owner", "Guest"];
const notificationCategoryOptions = [
  "Service & Maintenance",
  "Offers & Promotions",
  "Other",
];
const addNotification = {
  role: "",
  title: "",
  body: "",
  titleArabic: "",
  bodyArabic: "",
  brand: "",
  scheduleDate: "",
  expiryDate: "",
  extraInfo: {
    image_url: "",
    action_type: ACTION_TYPE_NONE,
    label: "",
    link: "",
    notificationCategory: "",
    redirectionScreen: DEFAULT_REDIRECTION_SCREEN,
    isButtonLayout: false,
  },
};
const errorObj = {
  role: "User Type",
  title: "Header text key",
  body: "Sub text key",
  notificationCategory: "Notification category",
  label: "Label",
  link: "Link URL",
  titleArabic: "Header text key",
  bodyArabic: "Sub text key",
  scheduleDate: "Schedule Date",
  expiryDate: "Expiry Date",
};

const mapRowDataToNotiState = (rowData: Record<string, unknown>) => {
  const extra = (rowData?.extraInfo ?? {}) as Record<string, unknown>;
  return {
    role: (rowData?.role as string) ?? "",
    title: (rowData?.title as string) ?? "",
    body:
      (extra?.bodyHTML as string) ??
      (rowData?.body as string) ??
      "",
    bodyArabic:
      (extra?.bodyArabicHTML as string) ??
      (rowData?.bodyArabic as string) ??
      "",
    titleArabic: (rowData?.titleArabic as string) ?? "",
    brand: (rowData?.brand as string) ?? "",
    scheduleDate:
      (rowData?.scheduledTime as string) ??
      (rowData?.scheduleDate as string) ??
      (extra?.scheduledTime as string) ??
      (extra?.scheduleDate as string) ??
      "",
    expiryDate:
      (rowData?.expiryTime as string) ??
      (rowData?.expiryDate as string) ??
      (extra?.expiryTime as string) ??
      (extra?.expiryDate as string) ??
      "",
    extraInfo: {
      image_url: (extra?.image_url as string) ?? "",
      action_type: (extra?.action_type as string) ?? ACTION_TYPE_NONE,
      label: (extra?.label as string) ?? "",
      link: (extra?.link as string) ?? "",
      notificationCategory:
        (extra?.notificationCategory as string) ??
        (rowData?.notificationType as string) ??
        "",
      redirectionScreen:
        (extra?.redirectionScreen as string) ?? DEFAULT_REDIRECTION_SCREEN,
      isButtonLayout: Boolean(extra?.isButtonLayout),
    },
  };
};

function AddNotification(): ReactElement {
  const history = useHistory();
  const location = useLocation<{
    detail?: string;
    rowData?: Record<string, unknown>;
  }>();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [triggerLoading, setTriggerLoading] = useState<boolean>(false);
  const ref = useRef<string | undefined>();
  const [notiState, setNotiState] = useState({ ...addNotification });

  const [notificationErrors, setnotificationErrors] = useState({
    roleError: "",
    titleError: "",
    bodyError: "",
    notificationTypeError: "",
    labelError: "",
    linkError: "",
    titleArabicError: "",
    bodyArabicError: "",
    scheduleDateError: "",
    expiryDateError: "",
  });
  const brandName = useAppSelector((state: RootState) => {
    return state.brand.brandType;
  });

  useEffect(() => {
    if (location?.state?.detail === "copy") {
      if (ref.current !== undefined && ref.current !== brandName) {
        history.push(NOTIFICATION);
        return;
      }
      ref.current = brandName;
    }
  }, [brandName, history, location?.state?.detail]);

  useEffect(() => {
    if (location?.state?.detail === "copy" && location?.state?.rowData) {
      setNotiState({
        ...mapRowDataToNotiState(location.state.rowData),
        brand: brandName,
      });
      setnotificationErrors({
        roleError: "",
        titleError: "",
        bodyError: "",
        notificationTypeError: "",
        labelError: "",
        linkError: "",
        titleArabicError: "",
        bodyArabicError: "",
        scheduleDateError: "",
        expiryDateError: "",
      });
    }
  }, [location?.state?.rowData, location?.state?.detail, brandName]);

  const handelChange = (props) => (e) => {
    const { value } = e.target;
    notiState[props] = value;
    if (value === "") {
      notificationErrors[props + "Error"] = `${errorObj[props]} ${EMPTY_INPUT}`;
    } else {
      notificationErrors[props + "Error"] = "";
    }
    if (title.length > 60) {
      notificationErrors[
        "titleError"
      ] = `${errorObj["title"]} ${INVALID_LENGTH_TITLE_INPUT}`;
    }
    if (body.length > NOTIFICATION_BODY_MAX_LENGTH) {
      notificationErrors[
        "bodyError"
      ] = `${errorObj["body"]} ${NOTIFICATION_BODY_LENGTH_ERROR}`;
    }
    setNotiState({ ...notiState, brand: brandName });
    setnotificationErrors({ ...notificationErrors });
  };

  const handleNotificationCategoryChange = (
    e: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const value = String(e.target.value ?? "");
    if (value === "") {
      notificationErrors.notificationTypeError = `${errorObj["notificationCategory"]} ${EMPTY_INPUT}`;
    } else {
      notificationErrors.notificationTypeError = "";
    }
    setNotiState((prev) => ({
      ...prev,
      extraInfo: {
        ...(prev.extraInfo ?? {}),
        notificationCategory: value,
      },
      brand: brandName,
    }));
    setnotificationErrors({ ...notificationErrors });
  };

  const handleActionTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setNotiState((prev) => ({
      ...prev,
      extraInfo: {
        ...(prev.extraInfo ?? {}),
        action_type: value,
        ...(value === ACTION_TYPE_NONE
          ? {
              label: "",
              link: "",
              redirectionScreen: DEFAULT_REDIRECTION_SCREEN,
            }
          : value === ACTION_TYPE_BUTTON
          ? { link: "" }
          : {}),
      },
      brand: brandName,
    }));
    setnotificationErrors((prev) => ({
      ...prev,
      labelError: "",
      linkError: "",
    }));
  };

  const handelExtraInfoField =
    (field: "label" | "link") => (e: React.ChangeEvent<HTMLInputElement>) => {
      let { value } = e.target;
      if (field === "label") {
        value = value.slice(0, ACTION_LABEL_MAX_LENGTH);
      }
      const errKey = field === "label" ? "labelError" : "linkError";
      if (value === "") {
        notificationErrors[errKey] = `${errorObj[field]} ${EMPTY_INPUT}`;
      } else {
        notificationErrors[errKey] = "";
      }
      setNotiState((prev) => ({
        ...prev,
        extraInfo: { ...(prev.extraInfo ?? {}), [field]: value },
        brand: brandName,
      }));
      setnotificationErrors({ ...notificationErrors });
    };

  const handleRedirectionScreenChange = (
    e: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const value = String(e.target.value ?? "");
    setNotiState((prev) => ({
      ...prev,
      extraInfo: {
        ...(prev.extraInfo ?? {}),
        redirectionScreen: value,
      },
      brand: brandName,
    }));
  };

  const handleIsButtonLayoutChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setNotiState((prev) => ({
      ...prev,
      extraInfo: {
        ...(prev.extraInfo ?? {}),
        isButtonLayout: checked,
      },
      brand: brandName,
    }));
  };

  const handleDateChange = (
    field: "scheduleDate" | "expiryDate",
    date: any
  ) => {
    if (!date) {
      setNotiState((prev) => ({
        ...prev,
        [field]: "",
        brand: brandName,
      }));
      setnotificationErrors((prev) => ({
        ...prev,
        [`${field}Error`]: "",
      }));
      return;
    }

    const d = new Date(date);
    if (!isNaN(d.getTime())) {
      if (field === "scheduleDate" && d.getHours() === 0 && d.getMinutes() === 0) {
        d.setHours(12, 1, 0, 0);
      } else if (field === "expiryDate" && d.getHours() === 0 && d.getMinutes() === 0) {
        d.setHours(23, 59, 0, 0);
      }
    }

    const formattedDate = !isNaN(d.getTime()) ? String(d) : "";
    setNotiState((prev) => ({
      ...prev,
      [field]: formattedDate,
      brand: brandName,
    }));
    setnotificationErrors((prev) => ({
      ...prev,
      [`${field}Error`]: "",
    }));
  };

  const handelSave = async (triggerNotificationToUsers = false) => {
    if (saveLoading || triggerLoading) {
      return;
    }
    if (role === "") {
      notificationErrors["roleError"] = `${errorObj["role"]} ${EMPTY_INPUT}`;
    }
    if (title === "") {
      notificationErrors["titleError"] = `${errorObj["title"]} ${EMPTY_INPUT}`;
    }
    const bodyTextLength = getRawTextLength(body);
    if (body === "" || bodyTextLength === 0) {
      notificationErrors["bodyError"] = `${errorObj["body"]} ${EMPTY_INPUT}`;
    } else if (bodyTextLength > NOTIFICATION_BODY_MAX_LENGTH) {
      notificationErrors[
        "bodyError"
      ] = `${errorObj["body"]} ${NOTIFICATION_BODY_LENGTH_ERROR}`;
    }
    if (title.length > 60) {
      notificationErrors[
        "titleError"
      ] = `${errorObj["title"]} ${INVALID_LENGTH_TITLE_INPUT}`;
    }
    if (titleArabic.length > 60) {
      notificationErrors[
        "titleArabicError"
      ] = `${errorObj["titleArabic"]} ${INVALID_LENGTH_TITLE_INPUT}`;
    }
    if (
      typeof bodyArabic === "string" &&
      getRawTextLength(bodyArabic) > NOTIFICATION_BODY_MAX_LENGTH
    ) {
      notificationErrors[
        "bodyArabicError"
      ] = `${errorObj["bodyArabic"]} ${NOTIFICATION_BODY_LENGTH_ERROR}`;
    }
    if (!notiState.extraInfo?.notificationCategory?.trim()) {
      notificationErrors[
        "notificationTypeError"
      ] = `${errorObj["notificationCategory"]} ${EMPTY_INPUT}`;
    }
    notificationErrors.scheduleDateError = "";
    notificationErrors.expiryDateError = "";

    if (!triggerNotificationToUsers && !notiState.scheduleDate?.trim()) {
      notificationErrors[
        "scheduleDateError"
      ] = `${errorObj["scheduleDate"]} ${EMPTY_INPUT}`;
    }

    if (triggerNotificationToUsers && notiState.scheduleDate?.trim()) {
      notificationErrors[
        "scheduleDateError"
      ] = "Please clear Schedule Date to trigger notification immediately";
      setnotificationErrors({ ...notificationErrors });
      return;
    }

    let scheduleD = notiState.scheduleDate ? new Date(notiState.scheduleDate) : null;
    if (scheduleD && !isNaN(scheduleD.getTime())) {
      if (scheduleD.getHours() === 0 && scheduleD.getMinutes() === 0) {
        scheduleD.setHours(12, 1, 0, 0);
      }
    }

    let expiryD = notiState.expiryDate ? new Date(notiState.expiryDate) : null;
    if (expiryD && !isNaN(expiryD.getTime())) {
      if (expiryD.getHours() === 0 && expiryD.getMinutes() === 0) {
        expiryD.setHours(23, 59, 0, 0);
      }
    }

    const nowUtc = new Date();
    let scheduledUtc: Date | null = null;
    let expiryUtc: Date | null = null;

    if (scheduleD && !isNaN(scheduleD.getTime())) {
      scheduledUtc = convertBahrainTimeToUtc(scheduleD);
      if (!triggerNotificationToUsers && scheduledUtc.getTime() < nowUtc.getTime()) {
        notificationErrors["scheduleDateError"] =
          "Scheduled time cannot be before current time";
      }
    }

    if (expiryD && !isNaN(expiryD.getTime())) {
      expiryUtc = convertBahrainTimeToUtc(expiryD);
      if (scheduledUtc && expiryUtc.getTime() < scheduledUtc.getTime()) {
        notificationErrors["expiryDateError"] =
          "Expiry time cannot be before scheduled time";
      } else if (!scheduledUtc && expiryUtc.getTime() < nowUtc.getTime()) {
        notificationErrors["expiryDateError"] =
          "Expiry time cannot be before current time";
      }
    }

    const hasDateErrors =
      Boolean(notificationErrors.scheduleDateError) ||
      Boolean(notificationErrors.expiryDateError);

    if (hasDateErrors) {
      setnotificationErrors({ ...notificationErrors });
      return;
    }

    notificationErrors.labelError = "";
    notificationErrors.linkError = "";
    let actionOk = true;
    const at = notiState.extraInfo?.action_type ?? ACTION_TYPE_NONE;
    if (at === ACTION_TYPE_BUTTON) {
      const lbl = notiState.extraInfo?.label?.trim() ?? "";
      if (!lbl) {
        notificationErrors.labelError = `Button label ${EMPTY_INPUT}`;
        actionOk = false;
      } else if (lbl.length > ACTION_LABEL_MAX_LENGTH) {
        notificationErrors.labelError = `${errorObj.label} ${INVALID_LENGTH_LABEL_INPUT}`;
        actionOk = false;
      }
    } else if (at === ACTION_TYPE_LINK) {
      const lbl = notiState.extraInfo?.label?.trim() ?? "";
      if (!lbl) {
        notificationErrors.labelError = `Link label ${EMPTY_INPUT}`;
        actionOk = false;
      } else if (lbl.length > ACTION_LABEL_MAX_LENGTH) {
        notificationErrors.labelError = `${errorObj.label} ${INVALID_LENGTH_LABEL_INPUT}`;
        actionOk = false;
      }
      if (!notiState.extraInfo?.link?.trim()) {
        notificationErrors.linkError = `${errorObj["link"]} ${EMPTY_INPUT}`;
        actionOk = false;
      }
    }

    const isScheduleValid = triggerNotificationToUsers || Boolean(notiState.scheduleDate?.trim());

    if (
      role !== "" &&
      title !== "" &&
      body !== "" &&
      notiState.extraInfo?.notificationCategory?.trim() &&
      isScheduleValid &&
      actionOk
    ) {
      try {
        if (triggerNotificationToUsers) {
          setTriggerLoading(true);
        } else {
          setSaveLoading(true);
        }
        const ei = notiState.extraInfo;
        const extraInfoPayload: NotificationObject["extraInfo"] = {
          action_type: ei?.action_type ?? ACTION_TYPE_NONE,
          redirection: EXTRA_INFO_REDIRECTION_IN_APP,
          notificationCategory: (ei?.notificationCategory ?? "").trim(),
        };
        if (ei?.image_url?.trim()) {
          extraInfoPayload.image_url = resolveImageUrlForApi(
            ei.image_url
          );
        }
        if (ei?.action_type === ACTION_TYPE_BUTTON && ei?.label?.trim()) {
          extraInfoPayload.label = ei.label.trim();
          extraInfoPayload.redirectionScreen =
            (ei?.redirectionScreen ?? DEFAULT_REDIRECTION_SCREEN).trim() ||
            DEFAULT_REDIRECTION_SCREEN;
        }
        if (
          ei?.action_type === ACTION_TYPE_LINK &&
          ei?.label?.trim() &&
          ei?.link?.trim()
        ) {
          extraInfoPayload.label = ei.label.trim();
          extraInfoPayload.link = ei.link.trim();
          extraInfoPayload.isButtonLayout = Boolean(ei?.isButtonLayout);
        }

        const isHtmlStyled = (str: string | null | undefined): boolean => {
          if (!str) return false;
          return /<[a-z][\s\S]*>/i.test(str);
        };

        if (notiState.body && isHtmlStyled(notiState.body)) {
          extraInfoPayload.bodyHTML = notiState.body;
        }
        if (notiState.bodyArabic && isHtmlStyled(notiState.bodyArabic)) {
          extraInfoPayload.bodyArabicHTML = notiState.bodyArabic;
        }

        const plainBody = getRawText(notiState.body);
        const plainBodyArabic = getRawText(notiState.bodyArabic);

        const formattedScheduleDate = scheduledUtc
          ? scheduledUtc.toISOString()
          : "";
        const formattedExpiryDate = expiryUtc
          ? expiryUtc.toISOString()
          : "";

        const requestPayload: NotificationObject = {
          role: notiState.role,
          title: notiState.title,
          body: plainBody,
          titleArabic: notiState.titleArabic,
          bodyArabic: plainBodyArabic,
          brand: brandName,
          scheduledTime: formattedScheduleDate,
          expiryTime: formattedExpiryDate,
          extraInfo: extraInfoPayload,
        };
        var AddNewResult = await dispatch(
          addNewNotificationThunk({
            notification: requestPayload,
            triggerNotificationToUsers,
          })
        ).unwrap();
        enqueueSnackbar(AddNewResult.data.status, {
          variant: "success",
        });
        if (triggerNotificationToUsers) {
          setTriggerLoading(false);
        } else {
          setSaveLoading(false);
        }
        history.push({
          pathname: `${NOTIFICATION}`,
        });
        setNotiState({
          role: "",
          title: "",
          brand: "",
          body: "",
          titleArabic: "",
          bodyArabic: "",
          scheduleDate: "",
          expiryDate: "",
          extraInfo: {
            image_url: "",
            action_type: ACTION_TYPE_NONE,
            label: "",
            link: "",
            notificationCategory: "",
            redirectionScreen: DEFAULT_REDIRECTION_SCREEN,
            isButtonLayout: false,
          },
        });
      } catch (error: any) {
        if (triggerNotificationToUsers) {
          setTriggerLoading(false);
        } else {
          setSaveLoading(false);
        }
        enqueueSnackbar(error?.data, { variant: "error" });
      }
    }
    setnotificationErrors({ ...notificationErrors });
  };

  const handleArabicText = (text, type) => {
    if (type === "hide") {
      notiState[text] = null;
      notificationErrors[text + "Error"] = "";
    } else {
      notiState[text] = "";
    }
    setNotiState({ ...notiState });
    setnotificationErrors({ ...notificationErrors });
  };
  const handleArabicChange = (e, prop) => {
    const { value } = e.target;
    // const result = new RegExp(/^[\u0621-\u064A\u0660-\u0669 ]+$/).test(value);
    // if (!result) {
    //   notificationErrors[prop + "Error"] = `Invalid ${errorObj[prop]}`;
    // } else {
    //   notificationErrors[prop + "Error"] = ``;
    // }
    if (titleArabic.length > 60) {
      notificationErrors[
        "titleArabicError"
      ] = `${errorObj["titleArabic"]} ${INVALID_LENGTH_TITLE_INPUT}`;
    }
    if (
      typeof bodyArabic === "string" &&
      bodyArabic.length > NOTIFICATION_BODY_MAX_LENGTH
    ) {
      notificationErrors[
        "bodyArabicError"
      ] = `${errorObj["bodyArabic"]} ${NOTIFICATION_BODY_LENGTH_ERROR}`;
    }
    notiState[prop] = value;
    setNotiState({ ...notiState });
    setnotificationErrors({ ...notificationErrors });
  };

  const handelCancel = () => {
    history.push(NOTIFICATION);
  };

  const imageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setNotiState((prev) => ({
        ...prev,
        extraInfo: {
          ...(prev.extraInfo ?? {}),
          image_url: reader.result as string,
        },
        brand: brandName,
      }));
    };
    e.target.value = "";
  };

  const handelRemoveImage = () => {
    setNotiState((prev) => ({
      ...prev,
      extraInfo: {
        ...(prev.extraInfo ?? {}),
        image_url: "",
      },
    }));
  };

  const {
    role,
    title,
    body,
    titleArabic,
    bodyArabic,
    scheduleDate,
    expiryDate,
    extraInfo,
  } = notiState;
  const notificationCategory = extraInfo?.notificationCategory ?? "";
  const actionType = extraInfo?.action_type ?? ACTION_TYPE_NONE;
  const label = extraInfo?.label ?? "";
  const link = extraInfo?.link ?? "";
  const redirectionScreen =
    extraInfo?.redirectionScreen ?? DEFAULT_REDIRECTION_SCREEN;
  const notificationImagePreview = extraInfo?.image_url?.trim();

  const {
    roleError,
    titleError,
    bodyError,
    notificationTypeError,
    labelError,
    linkError,
    titleArabicError,
    bodyArabicError,
    scheduleDateError,
    expiryDateError,
  } = notificationErrors;

  return (
    <PaperComponent
      heading={
        location?.state?.detail === "copy"
          ? "Copy Notification"
          : "Notification"
      }
    >
      <NotificationContainer>
        <FieldContainer>
          <InputLabelText>
            User Type<Required>*</Required>
          </InputLabelText>
          <UserTypeDropdown
            onChange={handelChange("role")}
            variant="outlined"
            value={role}
            error={Boolean(roleError)}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              getContentAnchorEl: null,
            }}
          >
            <MenuItem value="User Type" disabled>
              User Type
            </MenuItem>
            {userArr.map((item, index) => {
              return (
                <MenuItem key={`${item}_${index}`} value={item}>
                  {item}
                </MenuItem>
              );
            })}
          </UserTypeDropdown>
          {Boolean(roleError) && <ErrorHelperText>{roleError}</ErrorHelperText>}

          {/* {roleInArabic === null && (
            <EnterArabicText
              handleArabicText={handleArabicText}
              typeOfField={"roleInArabic"}
            />
          )} */}
        </FieldContainer>
        {/* <FieldContainer>
          {typeof roleInArabic === "string" && (
            <>
              <ArabicInput
                labelText={"User Type In "}
                value={roleInArabic}
                errorMessage={roleInArabicError}
                typeOfField={"roleInArabic"}
                handleArabicText={handleArabicText}
                handleArabicChange={handleArabicChange}
                width={"35%"}
              />
            </>
          )}
        </FieldContainer> */}

        <FieldContainer>
          <InputLabelText>
            Header text key<Required>*</Required> (must be less than 60
            character)
          </InputLabelText>

          <InputTextField
            value={title}
            variant="outlined"
            size="small"
            onChange={handelChange("title")}
            error={Boolean(titleError)}
            helperText={titleError}
            inputProps={{
              style: { fontSize: 14, fontFamily: PRIMARY_REGULAR_FONT },
            }}
          />
          {titleArabic === null && (
            <EnterArabicText
              handleArabicText={handleArabicText}
              typeOfField={"titleArabic"}
            />
          )}
        </FieldContainer>
        <FieldContainer>
          {typeof titleArabic === "string" && (
            <>
              <ArabicInput
                labelText={"Header Text In "}
                value={titleArabic}
                errorMessage={titleArabicError}
                typeOfField={"titleArabic"}
                handleArabicText={handleArabicText}
                handleArabicChange={handleArabicChange}
                width={"35%"}
              />
            </>
          )}
        </FieldContainer>

        <FieldContainer>
          <InputLabelText>
            Sub text key<Required>*</Required> (must be less than{" "}
            {NOTIFICATION_BODY_MAX_LENGTH} character)
          </InputLabelText>
          <CustomRichTextEditor
            editorState={body}
            handelChangeEditorState={(content: string) => {
              setNotiState((prev) => ({
                ...prev,
                body: content,
                brand: brandName,
              }));
              const textLen = getRawTextLength(content);
              if (!content || textLen === 0) {
                setnotificationErrors((prev) => ({
                  ...prev,
                  bodyError: `${errorObj["body"]} ${EMPTY_INPUT}`,
                }));
              } else if (textLen > NOTIFICATION_BODY_MAX_LENGTH) {
                setnotificationErrors((prev) => ({
                  ...prev,
                  bodyError: `${errorObj["body"]} ${NOTIFICATION_BODY_LENGTH_ERROR}`,
                }));
              } else {
                setnotificationErrors((prev) => ({
                  ...prev,
                  bodyError: "",
                }));
              }
            }}
            pluginArray={ALL_PLUGINS}
            customToolBar={SUB_TEXT_TOOLBAR}
            quickAccessToolbar={SUB_TEXT_TOOLBAR}
            editorHeight={220}
          />
          {Boolean(bodyError) && (
            <ErrorHelperText>{bodyError}</ErrorHelperText>
          )}
          {bodyArabic === null && (
            <EnterArabicText
              handleArabicText={handleArabicText}
              typeOfField={"bodyArabic"}
            />
          )}
        </FieldContainer>
        <FieldContainer>
          {typeof bodyArabic === "string" && (
            <>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "0.625rem" }}>
                <InputLabelText style={{ marginBottom: 0, marginRight: "0.5rem" }}>
                  Sub Text Key In (Arabic)
                </InputLabelText>
                <CancelInput
                  onClick={() => handleArabicText("bodyArabic", "hide")}
                  style={{ margin: 0, cursor: "pointer" }}
                />
              </div>
              <CustomRichTextEditor
                editorState={bodyArabic}
                handelChangeEditorState={(content: string) => {
                  setNotiState((prev) => ({
                    ...prev,
                    bodyArabic: content,
                    brand: brandName,
                  }));
                  const textLen = getRawTextLength(content);
                  if (textLen > NOTIFICATION_BODY_MAX_LENGTH) {
                    setnotificationErrors((prev) => ({
                      ...prev,
                      bodyArabicError: `${errorObj["bodyArabic"]} ${NOTIFICATION_BODY_LENGTH_ERROR}`,
                    }));
                  } else {
                    setnotificationErrors((prev) => ({
                      ...prev,
                      bodyArabicError: "",
                    }));
                  }
                }}
                directionality="rtl"
                pluginArray={ALL_PLUGINS}
                customToolBar={SUB_TEXT_TOOLBAR}
                quickAccessToolbar={SUB_TEXT_TOOLBAR}
                editorHeight={220}
              />
              {Boolean(bodyArabicError) && (
                <ErrorHelperText>{bodyArabicError}</ErrorHelperText>
              )}
            </>
          )}
        </FieldContainer>

        <FieldContainer>
          <InputLabelText>
            Notification category<Required>*</Required>
          </InputLabelText>
          <NotificationTypeDropdown
            onChange={handleNotificationCategoryChange}
            variant="outlined"
            value={notificationCategory}
            error={Boolean(notificationTypeError)}
            displayEmpty
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              getContentAnchorEl: null,
            }}
          >
            <MenuItem value="" disabled>
              Select notification category
            </MenuItem>
            {notificationCategoryOptions.map((item, index) => (
              <MenuItem key={`${item}_${index}`} value={item}>
                {item}
              </MenuItem>
            ))}
          </NotificationTypeDropdown>
          {Boolean(notificationTypeError) && (
            <ErrorHelperText>{notificationTypeError}</ErrorHelperText>
          )}
        </FieldContainer>

        <FieldContainer>
          <InputLabelText>Action Type</InputLabelText>
          <FormControl component="fieldset">
            <RadioGroup
              row
              name="notificationActionType"
              value={actionType}
              onChange={handleActionTypeChange}
            >
              <FormControlLabel
                value={ACTION_TYPE_LINK}
                control={<Radio color="primary" size="small" />}
                label="Link"
              />
              <FormControlLabel
                value={ACTION_TYPE_BUTTON}
                control={<Radio color="primary" size="small" />}
                label="Button"
              />
              <FormControlLabel
                value={ACTION_TYPE_NONE}
                control={<Radio color="primary" size="small" />}
                label="None"
              />
            </RadioGroup>
          </FormControl>
        </FieldContainer>

        <FieldContainer>
          <InputLabelText>
            Schedule Date
          </InputLabelText>
          <MaterialUIPickers
            time={scheduleDate}
            handelChangeParent={(d) => handleDateChange("scheduleDate", d)}
            errorType={scheduleDateError}
            defaultTime={{ hours: 12, minutes: 1 }}
            width={35}
          />
          {Boolean(scheduleDateError) && (
            <ErrorHelperText>{scheduleDateError}</ErrorHelperText>
          )}
        </FieldContainer>

        <FieldContainer>
          <InputLabelText>Expiry Date</InputLabelText>
          <MaterialUIPickers
            time={expiryDate}
            handelChangeParent={(d) => handleDateChange("expiryDate", d)}
            errorType={expiryDateError}
            defaultTime={{ hours: 23, minutes: 59 }}
            width={35}
          />
          {Boolean(expiryDateError) && (
            <ErrorHelperText>{expiryDateError}</ErrorHelperText>
          )}
        </FieldContainer>

        {actionType === ACTION_TYPE_BUTTON && (
          <>
            <FieldContainer>
              <InputLabelText>
                Button label<Required>*</Required> (max{" "}
                {ACTION_LABEL_MAX_LENGTH} characters)
              </InputLabelText>
              <InputTextField
                value={label}
                variant="outlined"
                size="small"
                onChange={handelExtraInfoField("label")}
                error={Boolean(labelError)}
                helperText={labelError}
                inputProps={{
                  maxLength: ACTION_LABEL_MAX_LENGTH,
                  style: { fontSize: 14, fontFamily: PRIMARY_REGULAR_FONT },
                }}
              />
            </FieldContainer>
            <FieldContainer>
              <InputLabelText>
                Select App Screen Name to Navigate
              </InputLabelText>
              <NotificationTypeDropdown
                onChange={handleRedirectionScreenChange}
                variant="outlined"
                value={redirectionScreen}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
              >
                {APP_SCREEN_REDIRECTION_OPTIONS.map((item, index) => (
                  <MenuItem key={`${item}_${index}`} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </NotificationTypeDropdown>
            </FieldContainer>
          </>
        )}

        {actionType === ACTION_TYPE_LINK && (
          <>
            <FieldContainer>
              <InputLabelText>
                Link label<Required>*</Required> (max{" "}
                {ACTION_LABEL_MAX_LENGTH} characters)
              </InputLabelText>
              <InputTextField
                value={label}
                variant="outlined"
                size="small"
                onChange={handelExtraInfoField("label")}
                error={Boolean(labelError)}
                helperText={labelError}
                inputProps={{
                  maxLength: ACTION_LABEL_MAX_LENGTH,
                  style: { fontSize: 14, fontFamily: PRIMARY_REGULAR_FONT },
                }}
              />
            </FieldContainer>

            <FieldContainer>
              <InputLabelText>
                Link URL<Required>*</Required>
              </InputLabelText>
              <InputTextField
                value={link}
                variant="outlined"
                size="small"
                onChange={handelExtraInfoField("link")}
                error={Boolean(linkError)}
                helperText={linkError}
                inputProps={{
                  style: { fontSize: 14, fontFamily: PRIMARY_REGULAR_FONT },
                }}
              />
            </FieldContainer>
                        <FieldContainer>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(extraInfo?.isButtonLayout)}
                    onChange={handleIsButtonLayoutChange}
                    color="primary"
                    size="small"
                  />
                }
                label="Show Link as Button"
              />
            </FieldContainer>
          </>
        )}

        <ImgWholeContainerDeals>
          <InputLabelText>Image</InputLabelText>
          <ImageFlexContainer>
            <ImageInput
              type="file"
              accept=".png, .jpg, .jpeg"
              onChange={imageHandler}
              id="notificationImagesInput"
            />
            <AddImageContainer htmlFor="notificationImagesInput">
              <AddImage />
            </AddImageContainer>
            {Boolean(notificationImagePreview) && (
              <ChildImageContainer>
                <ChildImage
                  src={notificationImagePreview}
                  alt="Notification image"
                />
                <RemoveImageLogo onClick={handelRemoveImage} />
              </ChildImageContainer>
            )}
          </ImageFlexContainer>
        </ImgWholeContainerDeals>

        <ActionButtonsRow>
          <LoaderButton
            isLoading={saveLoading}
            disabled={saveLoading || triggerLoading}
            buttonText="Schedule Notification"
            styles={loaderBtnStyleFunc("auto", "2.6rem")}
            submitHandler={() => handelSave(false)}
          />
          <LoaderButton
            isLoading={triggerLoading}
            disabled={saveLoading || triggerLoading}
            buttonText="Trigger Notification"
            styles={loaderBtnStyleFunc("auto", "2.6rem")}
            className="trigger-notification-btn"
            submitHandler={() => handelSave(true)}
          />
          <CancelButtonSec variant="outlined" color="primary" onClick={handelCancel}>
            Cancel
          </CancelButtonSec>
        </ActionButtonsRow>
      </NotificationContainer>
    </PaperComponent>
  );
}

export default AddNotification;
